import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../ui/Dropdown';
import { useLanguage } from '../../../localization/useLanguage';
import { CHAT_COUNT_DISPLAY_MAX, CHAT_FILTER, CHAT_FILTER_ORDER } from '../../../Helpers/Chat/Constant';
import { IconChatTags } from '../../../Components/Common/Icons';
import styles from './index.module.scss';

const LABEL_KEY = {
    [CHAT_FILTER.ALL]: 'chat.filter.ALL',
    [CHAT_FILTER.UNREAD]: 'chat.filter.UNREAD',
    [CHAT_FILTER.FAVORITES]: 'chat.filter.FAVORITES',
    [CHAT_FILTER.GROUPS]: 'chat.filter.GROUPS',
    [CHAT_FILTER.COMMUNITIES]: 'chat.filter.COMMUNITIES',
    [CHAT_FILTER.CHANNELS]: 'chat.filter.CHANNELS',
};

const formatCount = count => (count > CHAT_COUNT_DISPLAY_MAX ? `${CHAT_COUNT_DISPLAY_MAX}+` : count);

/**
 * Right-click menu on a chat tag.
 *
 * "All" cannot be removed, so it offers editing only. Both actions lead to the Chat Tag
 * screen FR188 will bring, which is why they share a handler for now.
 *
 * @param menu Which chip was right-clicked, and the pointer position to anchor to.
 * @param onOpenChange Called when the menu closes.
 * @param onManageTags Opens the chat tag screen.
 */
const TagContextMenu = ({ menu, onOpenChange, onManageTags }) => {
    const { t } = useLanguage();

    return (
        <DropdownMenu open={Boolean(menu.filter)} onOpenChange={onOpenChange}>
            <DropdownMenuTrigger
                aria-hidden
                tabIndex={-1}
                className={styles.tagMenuAnchor}
                style={{ left: menu.x, top: menu.y }}
            />
            <DropdownMenuContent align="start" sideOffset={0} className={styles.tagMenu}>
                <DropdownMenuItem onSelect={onManageTags}>{t('chat.filter.EDIT_TAGS')}</DropdownMenuItem>
                {menu.filter !== CHAT_FILTER.ALL && (
                    <DropdownMenuItem onSelect={onManageTags}>{t('chat.filter.REMOVE_TAG')}</DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

/**
 * Filter chips above the recent chat list.
 *
 * @param active Currently selected filter.
 * @param counts Count per filter key.
 * @param onSelect Called with the filter the user picked.
 * @param onManageTags Called when the user opens the chat tag screen.
 */
export default function ChipsRow({ active, counts = {}, onSelect, onManageTags }) {
    const { t } = useLanguage();
    const scrollerRef = useRef(null);
    const [overflow, setOverflow] = useState({ start: false, end: false });
    // Which chip was right-clicked, and where to anchor its menu.
    const [tagMenu, setTagMenu] = useState({ filter: null, x: 0, y: 0 });

    const openTagMenu = (event, filter) => {
        event.preventDefault();
        setTagMenu({ filter, x: event.clientX, y: event.clientY });
    };

    const closeTagMenu = isOpen => {
        if (!isOpen) setTagMenu(prev => ({ ...prev, filter: null }));
    };

    // A one-pixel slack absorbs the fractional scroll widths zoom and DPI scaling produce,
    // which would otherwise leave the end fade showing at the very end of the track.
    const syncOverflow = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;

        const max = el.scrollWidth - el.clientWidth;
        setOverflow({ start: el.scrollLeft > 1, end: el.scrollLeft < max - 1 });
    }, []);

    // Runs before paint so the fade is right on the first frame, not one frame late.
    useLayoutEffect(syncOverflow, [syncOverflow, counts]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;

        // Chip labels are translated and the pane is resizable, so the overflow has to be
        // remeasured rather than decided once from the initial widths.
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(syncOverflow) : null;
        observer?.observe(el);
        window.addEventListener('resize', syncOverflow);

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', syncOverflow);
        };
    }, [syncOverflow]);

    /* Turns a plain wheel into horizontal scrolling: a mouse without a tilt wheel only ever
     * reports deltaY, which a horizontal scroller ignores, leaving the last chips
     * unreachable without a touchpad or a drag.
     *
     * Bound by hand because React attaches onWheel passively, where preventDefault is
     * ignored — and without it the page scrolls vertically at the same time. */
    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return undefined;

        const onWheel = event => {
            if (event.deltaY === 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;

            const max = el.scrollWidth - el.clientWidth;
            if (max <= 0) return;

            // Only claim the gesture while there is somewhere to go, so that at either end
            // the page keeps its normal vertical scroll.
            const next = Math.min(Math.max(el.scrollLeft + event.deltaY, 0), max);
            if (next === el.scrollLeft) return;

            event.preventDefault();
            el.scrollLeft = next;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    return (
        <div className={styles.row}>
            <div className={styles.scroller}>
                <span
                    className={`${styles.fade} ${styles.fadeStart} ${overflow.start ? styles.visible : ''}`}
                    aria-hidden="true"
                />
                <div
                    ref={scrollerRef}
                    className={styles.chips}
                    role="group"
                    aria-label={t('chat.filter.GROUP_LABEL')}
                    onScroll={syncOverflow}
                >
                    {CHAT_FILTER_ORDER.map(filter => {
                        const isActive = filter === active;
                        // "All" is the absence of a filter, so a count there says nothing useful.
                        const count = filter === CHAT_FILTER.ALL ? null : counts[filter] || 0;

                        return (
                            <button
                                key={filter}
                                type="button"
                                className={`${styles.chip} ${isActive ? styles.active : ''}`}
                                aria-pressed={isActive}
                                onClick={() => onSelect(filter)}
                                onContextMenu={event => openTagMenu(event, filter)}
                            >
                                {t(LABEL_KEY[filter])}
                                {count !== null && <span className={styles.count}>{formatCount(count)}</span>}
                            </button>
                        );
                    })}
                </div>
                <span
                    className={`${styles.fade} ${styles.fadeEnd} ${overflow.end ? styles.visible : ''}`}
                    aria-hidden="true"
                />
            </div>
            {/* Sits outside the scroller so it stays reachable however far the chips scroll. */}
            <button
                type="button"
                className={styles.manageTags}
                aria-label={t('chat.filter.MANAGE_TAGS')}
                title={t('chat.filter.MANAGE_TAGS')}
                onClick={onManageTags}
            >
                <IconChatTags />
            </button>

            <TagContextMenu menu={tagMenu} onOpenChange={closeTagMenu} onManageTags={onManageTags} />
        </div>
    );
}
